import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ResponsiveContainer, Cell, Bar, BarChart, Legend, Tooltip } from 'recharts';
import chroma from 'chroma-js';
import { useTranslation } from 'react-i18next';
import useWindowResize from 'customHooks/useWindowResize';
import ChartTooltip from 'components/ChartTooltip';

const I18N_BASE_PATH = 'pages.private.dashboard.components.charts.funnel';

const CustomBar = (props) => {
  const { x, y, width, height, fill, payload, index, data } = props;

  const [nextY, setNextY] = useState(y);

  useWindowResize(() => {
    const nextPoly = data.at(index + 1);

    if (nextPoly && height > 0) {
      const polyElement = document.getElementById(`poly-${nextPoly.step}`);

      if (polyElement) {
        setNextY(polyElement.points[0].y);
        return;
      }
    }

    setNextY(y);
  }, [data, height, index, y]);

  return (
    <g>
      <polygon
        id={`poly-${payload.step}`}
        points={`${x} ${y}, ${x + width + 1} ${nextY}, ${x + width + 1} ${
          y + height
        }, ${x} ${y + height}, ${x} ${y}`}
        fill={fill}
      />
    </g>
  );
};

const FunnelChart = (props) => {
  const { t: translate } = useTranslation();

  const { data } = props;
  const baseColors = [
    '#116541',
    '#178E5B',
    '#2DB279',
    '#34D690',
    '#34A5D6',
    '#4490FB',
    '#475EFF',
    '#044AEB',
  ];

  const colors = chroma.scale(baseColors).colors(data.length);

  const getStepName = (step) => {
    let narmalizedStepName = step;

    if (step.startsWith('STEP_')) narmalizedStepName = step.replace('STEP_', '');

    return narmalizedStepName.split('-')[0];
  };

  const legendData = useMemo(
    () =>
      data.map(({ step }, index) => ({
        step,
        color: colors[index],
        value: translate(`${I18N_BASE_PATH}.steps.${getStepName(step)}.description`),
        type: 'square',
      })),
    [colors, data, translate]
  );

  const renderCustomTooltip = (tooltipProps) => {
    const { active, payload } = tooltipProps;

    if (!active) return null;

    const { step, value } = payload[0].payload;

    return (
      <ChartTooltip
        subtitle={`${translate(
          `${I18N_BASE_PATH}.steps.${getStepName(step)}.description`
        )}: ${value}`}
      />
    );
  };

  return (
    <ResponsiveContainer width="95%">
      <BarChart
        data={data}
        barCategoryGap={0}
        style={{
          marginTop: -10,
          fontSize: '0.7rem',
        }}
      >
        <Bar dataKey="value" shape={<CustomBar data={data} />} isAnimationActive={false}>
          {data.map((entry, index) => (
            <Cell key={`cell-${entry.step}`} fill={colors[index]} />
          ))}
        </Bar>
        <Tooltip content={renderCustomTooltip} cursor={false} />
        <Legend
          wrapperStyle={{
            paddingTop: 15,
            paddingBottom: 10,
          }}
          iconSize={10}
          payload={legendData}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

FunnelChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      step: PropTypes.string,
      value: PropTypes.number,
    })
  ).isRequired,
};

CustomBar.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  fill: PropTypes.string.isRequired,
  payload: PropTypes.shape({
    step: PropTypes.string,
    value: PropTypes.number,
  }).isRequired,
  index: PropTypes.number.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      step: PropTypes.string,
      value: PropTypes.number,
    })
  ).isRequired,
};

export default FunnelChart;
